import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Help as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import axios from 'axios';
import { useAuth } from '@/context/AuthContextType';
import { useNavigate } from 'react-router-dom';

interface AWSSettings {
  aws_access_key: string;
  aws_secret_key: string;
  aws_region: string;
  aws_account_id: string;
  efs_databases: string;
  efs_params: string;
  input_bucket: string;
  output_bucket: string;
  key_name: string;
  security_group_id: string;
  subnet_id: string;
  ecr_repo?: string;
}

interface ValidationErrors {
  [key: string]: string;
}

// Helper function to handle API errors
const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error) && error.response) {
    const { data, status, statusText } = error.response;
    
    // Check if data is a string
    if (typeof data === 'string') {
      return data;
    }
    
    // Check if data is an object with error details
    if (data && typeof data === 'object') {
      if ('detail' in data && typeof data.detail === 'string') {
        return data.detail;
      }
      if ('error' in data && typeof data.error === 'string') {
        return data.error;
      }
    }
    
    // Fallback to status code
    return `Error: ${status} ${statusText}`;
  }
  
  // Generic error message
  return 'An unexpected error occurred. Please try again later.';
};

// Helper function to check if response is HTML
const isHtmlResponse = (data: unknown): boolean => {
  return typeof data === 'string' && data.includes('<!DOCTYPE html>');
};

const AWSSettings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<AWSSettings>({
    aws_access_key: '',
    aws_secret_key: '',
    aws_region: '',
    aws_account_id: '',
    efs_databases: '',
    efs_params: '',
    input_bucket: '',
    output_bucket: '',
    key_name: '',
    security_group_id: '',
    subnet_id: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    // Redirect non-admin users
    if (!user?.is_staff) {
      navigate('/jobs');
      return;
    }

    const fetchSettings = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await api.get('/api/user/aws-settings/');
        console.log('Settings response:', response.data);
        
        // Check if the response is HTML (indicating a redirect)
        if (isHtmlResponse(response.data)) {
          console.error('Received HTML response instead of JSON');
          setError('Received unexpected response from server. Please try again or contact support.');
          return;
        }
        
        if (response.data && response.data.length > 0) {
          const settings = Array.isArray(response.data) ? response.data[0] : response.data;
          console.log('Processed settings:', settings);
          setSettings(settings);
        } else {
          // Use empty values if no settings exist
          setSettings({
            aws_access_key: '',
            aws_secret_key: '',
            aws_region: '',
            aws_account_id: '',
            efs_databases: '',
            efs_params: '',
            input_bucket: '',
            output_bucket: '',
            key_name: '',
            security_group_id: '',
            subnet_id: ''
          });
        }
      } catch (error) {
        console.error('Error fetching AWS settings:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [user, navigate]);

  // Validation patterns
  const validationPatterns = {
    aws_account_id: /^\d{12}$/,
    aws_region: /^[a-z]{2}-[a-z]+-\d{1}$/,
    efs_databases: /^fs-[a-f0-9]{8,}$/,
    efs_params: /^fs-[a-f0-9]{8,}$/,
    security_group_id: /^sg-[a-f0-9]{8,}$/,
    subnet_id: /^subnet-[a-f0-9]{8,}$/,
  };

  // Validation messages
  const validationMessages = {
    aws_account_id: "Must be exactly 12 digits",
    aws_region: "Must be in format: us-east-1",
    efs_databases: "Must start with fs- followed by alphanumeric characters",
    efs_params: "Must start with fs- followed by alphanumeric characters",
    security_group_id: "Must start with sg- followed by alphanumeric characters",
    subnet_id: "Must start with subnet- followed by alphanumeric characters",
  };

  const validateField = (field: keyof AWSSettings, value: string): string | null => {
    if (field in validationPatterns) {
      const pattern = validationPatterns[field as keyof typeof validationPatterns];
      return pattern.test(value) ? null : validationMessages[field as keyof typeof validationMessages];
    }
    return null;
  };

  const handleFieldChange = (field: keyof AWSSettings, value: string) => {
    setSettings({ ...settings, [field]: value });
    const error = validateField(field, value);
    if (error) {
      setValidationErrors({ ...validationErrors, [field]: error });
    } else {
      const newErrors = { ...validationErrors };
      delete newErrors[field];
      setValidationErrors(newErrors);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResults({});
    setError(null);
    
    try {
      const response = await api.post('/api/user/aws-settings/test/', settings);
      console.log('Test response:', response.data);
      
      // Check if the response is HTML (indicating a redirect)
      if (isHtmlResponse(response.data)) {
        console.error('Received HTML response instead of JSON');
        setError('Received unexpected response from server. Please try again or contact support.');
        return;
      }
      
      setTestResults(response.data);
    } catch (error) {
      console.error('Connection test failed:', error);
      setError(handleApiError(error));
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Make the request - Axios should automatically include the CSRF token
      const response = await api.post('/api/user/aws-settings/', settings);
      console.log('Settings saved:', response.data);
      
      // Check if the response is HTML (indicating a redirect)
      if (isHtmlResponse(response.data)) {
        console.error('Received HTML response instead of JSON');
        setError('Received unexpected response from server. Please try again or contact support.');
        return;
      }
      
      setSuccess(true);
      
      // Refresh settings after saving
      try {
        const getResponse = await api.get('/api/user/aws-settings/');
        if (getResponse.data && getResponse.data.length > 0) {
          const updatedSettings = Array.isArray(getResponse.data) ? getResponse.data[0] : getResponse.data;
          setSettings(updatedSettings);
        }
      } catch (refreshError) {
        console.error('Error fetching updated settings:', refreshError);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const renderField = (
    label: string,
    value: string | undefined,
    field: keyof AWSSettings,
    tooltip: string,
    type: string = 'text'
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <TextField
        fullWidth
        label={label}
        type={type}
        name={field}
        value={value ?? ''}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        margin="normal"
        required
        error={!!validationErrors[field]}
        helperText={validationErrors[field]}
      />
      <Tooltip title={tooltip} arrow placement="right">
        <IconButton size="small" sx={{ 
          ml: 1,
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          }
        }}>
          <HelpIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
      {testResults[field] && (
        <Tooltip title="Verified">
          <CheckIcon color="success" sx={{ ml: 1 }} />
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Container maxWidth="sm">
      {!user?.is_staff ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Alert severity="error">
            You do not have permission to access AWS settings. This feature is only available to administrators.
          </Alert>
        </Box>
      ) : loading || !settings ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            AWS Settings
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Settings updated successfully!</Alert>}

          <form onSubmit={handleSubmit}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">AWS Credentials</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderField(
                  "AWS Access Key",
                  settings?.aws_access_key,
                  "aws_access_key",
                  "The access key ID from your AWS IAM user credentials"
                )}
                {renderField(
                  "AWS Secret Key",
                  settings?.aws_secret_key,
                  "aws_secret_key",
                  "The secret access key from your AWS IAM user credentials",
                  "password"
                )}
                {renderField(
                  "AWS Region",
                  settings?.aws_region,
                  "aws_region",
                  "The AWS region where your resources are located (e.g., us-east-1). Must match your infrastructure region."
                )}
                {renderField(
                  "AWS Account ID",
                  settings?.aws_account_id,
                  "aws_account_id",
                  "Your 12-digit AWS account ID. This is used to construct ECR repository URLs."
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Storage Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderField(
                  "Input Bucket",
                  settings?.input_bucket,
                  "input_bucket",
                  "The S3 bucket where job input files will be stored. Must exist and be accessible."
                )}
                {renderField(
                  "Output Bucket",
                  settings?.output_bucket,
                  "output_bucket",
                  "The S3 bucket where job results will be stored. Must exist and be accessible."
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">EFS Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderField(
                  "EFS Databases ID",
                  settings?.efs_databases,
                  "efs_databases",
                  "The EFS filesystem ID (fs-xxxxxx) containing the Colabfold databases. Must be mounted and accessible."
                )}
                {renderField(
                  "EFS Parameters ID",
                  settings?.efs_params,
                  "efs_params",
                  "The EFS filesystem ID (fs-xxxxxx) containing the Alphafold parameters. Must be mounted and accessible."
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">EC2 Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderField(
                  "Key Name",
                  settings?.key_name,
                  "key_name",
                  "The name of your EC2 key pair for SSH access. Must exist in the specified region."
                )}
                {renderField(
                  "Security Group ID",
                  settings?.security_group_id,
                  "security_group_id",
                  "The security group ID (sg-xxxxxx) that allows required network access. Must exist and have proper rules."
                )}
                {renderField(
                  "Subnet ID",
                  settings?.subnet_id,
                  "subnet_id",
                  "The subnet ID (subnet-xxxxxx) where EC2 instances will be launched. Must have internet access."
                )}
              </AccordionDetails>
            </Accordion>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={testConnection}
                disabled={testing || Object.keys(validationErrors).length > 0}
                startIcon={testing ? <CircularProgress size={20} /> : null}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={testing || Object.keys(validationErrors).length > 0}
              >
                Save Settings
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />
            
            <Box>
              <Typography variant="h6" gutterBottom>
                Import/Export Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Export your settings to a JSON file or import settings from a previously exported file.
              </Typography>
              
              <ImportExportButtons 
                settings={settings}
                onImport={(importedSettings) => {
                  setSettings(importedSettings);
                  setSuccess(true);
                }}
              />
            </Box>
          </form>
        </Paper>
      )}
    </Container>
  );
};

interface ImportExportButtonsProps {
  settings: AWSSettings;
  onImport: (settings: AWSSettings) => void;
}

const ImportExportButtons: React.FC<ImportExportButtonsProps> = ({ settings, onImport }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleExport = () => {
    // Create a JSON blob from settings
    const settingsJSON = JSON.stringify(settings, null, 2);
    const blob = new Blob([settingsJSON], { type: 'application/json' });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aws-settings-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    
    // Trigger download and cleanup
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    // Reset error
    setImportError(null);
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const importedSettings = JSON.parse(content) as AWSSettings;
        
        // Validate that the imported JSON has the required fields
        const requiredFields: (keyof AWSSettings)[] = [
          'aws_access_key', 'aws_secret_key', 'aws_region', 'aws_account_id',
          'input_bucket', 'output_bucket'
        ];
        
        const missingFields = requiredFields.filter(field => !importedSettings[field]);
        
        if (missingFields.length > 0) {
          setImportError(`Invalid settings file: Missing required fields: ${missingFields.join(', ')}`);
          return;
        }
        
        // Call onImport callback with the imported settings
        onImport(importedSettings);
      } catch (error) {
        console.error('Error importing settings:', error);
        setImportError('Invalid JSON file. Please upload a properly formatted settings file.');
      }
    };
    
    reader.onerror = () => {
      setImportError('Error reading the file. Please try again.');
    };
    
    reader.readAsText(file);
    
    // Reset the file input
    e.target.value = '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {importError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {importError}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
        >
          Export Settings
        </Button>
        
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<FileUploadIcon />}
          onClick={handleImportClick}
        >
          Import Settings
        </Button>
        
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".json"
          onChange={handleFileChange}
        />
      </Box>
    </Box>
  );
};

export default AWSSettings; 